/**
 * Chart valuation (M3 spec §6.2, plan R18): a system is worth `base[region] × novelty × first discovery`.
 * - Novelty is `prechartedMult` when the system was charted in the new-game knowledge, otherwise 1.
 * - First discovery is ×`firstDiscoveryMult` when the system was unknown in the new-game knowledge.
 * - Each POI adds `base × poiRichness` (a hidden one found by a scan: `hiddenPoiRichness`) with the same multipliers.
 * - An anchor system adds `base × anchorBonus` once; a surveyed anomaly adds its POI value × `anomalyMult`.
 * - The system value alone is also × its minerals factor (M8 MC-a8): `gradeMult[richest asteroid grade]`, × `signatureMult`
 *   when the system has a signature. The shipped factors are 1, so values are unchanged until MC-a6 sets them.
 * Values are whole credits. Pure functions.
 */
import { galaxyGradeIds } from '../data/galaxyConfig'
import type { RegionId } from '../galaxy/types'
import type { KnownLevel } from '../reach/state'
import type { ExploreConfig } from './config'
import type { MineralsKnowledge } from './minerals'

/** The system's multipliers, fixed by the new-game knowledge (recomputed on load, never saved). */
export interface ChartMultipliers {
  novelty: number
  first: number
}

export function chartMultipliers(
  initial: Readonly<Record<string, KnownLevel>>,
  systemId: string,
  cfg: ExploreConfig,
  out: ChartMultipliers = { novelty: 1, first: 1 },
): ChartMultipliers {
  const known = initial[systemId]
  out.novelty = known === 'charted' ? cfg.charts.prechartedMult : 1
  out.first = known === undefined ? cfg.charts.firstDiscoveryMult : 1
  return out
}

/** True when the system was unknown in the new-game knowledge (its charting is a first discovery). */
export function isFirstDiscovery(initial: Readonly<Record<string, KnownLevel>>, systemId: string): boolean {
  return initial[systemId] === undefined
}

function whole(v: number): number {
  return Number.isFinite(v) && v > 0 ? Math.round(v) : 0
}

/**
 * The minerals factor on a system's chart value (MC-a8): `gradeMult` at the richest grade among its asteroid deposits
 * (×1 without one) × `signatureMult` when it has a signature. An uncharted system's factor is 1.
 */
export function chartMineralFactor(minerals: MineralsKnowledge, cfg: ExploreConfig): number {
  if (minerals === 'uncharted') return 1
  let best = -1
  for (const d of minerals.deposits) best = Math.max(best, galaxyGradeIds.indexOf(d.grade))
  const grade = best < 0 ? 1 : cfg.charts.gradeMult[best]
  return grade * (minerals.signature === null ? 1 : cfg.charts.signatureMult)
}

/** The system's own chart value; `mineral` is its `chartMineralFactor` (1 leaves the M3 value unchanged). */
export function systemChartValue(region: RegionId, m: ChartMultipliers, cfg: ExploreConfig, mineral = 1): number {
  return whole(cfg.charts.base[region] * m.novelty * m.first * mineral)
}

/** The anchor bonus (charted once per anchor system). */
export function anchorChartValue(region: RegionId, m: ChartMultipliers, cfg: ExploreConfig): number {
  return whole(cfg.charts.base[region] * cfg.charts.anchorBonus * m.novelty * m.first)
}

/** One POI's chart richness: `hiddenPoiRichness` for a hidden POI, `poiRichness` otherwise. */
export function poiChartValue(region: RegionId, hidden: boolean, m: ChartMultipliers, cfg: ExploreConfig): number {
  const richness = hidden ? cfg.charts.hiddenPoiRichness : cfg.charts.poiRichness
  return whole(cfg.charts.base[region] * richness * m.novelty * m.first)
}

/** The survey bonus of an anomaly: its POI value × `anomalyMult`. */
export function anomalyChartValue(region: RegionId, hidden: boolean, m: ChartMultipliers, cfg: ExploreConfig): number {
  const richness = hidden ? cfg.charts.hiddenPoiRichness : cfg.charts.poiRichness
  return whole(cfg.charts.base[region] * richness * cfg.charts.anomalyMult * m.novelty * m.first)
}
